import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogTitle, IconButton, Link } from '@mui/material';
import {
	DataGridPremium,
	GridColDef,
	GridFilterModel,
	GridLogicOperator,
	GridPaginationModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridStringOperators,
} from '@mui/x-data-grid-premium';
import { useQuery } from '@parkchirp/api-client';
import { adaptGridValueCallback, isEmptyGridFilterValue } from '@parkchirp/ui-library';
import dynamic from 'next/dynamic';
import { useState } from 'react';

import { useDebounce } from '../../hooks/useDebounce';
import { LIST_ORPHAN_PAYMENT_LOGS } from '../../queries';
import { convertMuiFilterForV2ApiWithoutValue } from '../../utils';

const JSONViewer = dynamic(() => import('@microlink/react-json-view'), {
	ssr: false,
});

export const OrphanPaymentLogsTable = () => {
	const [{ page, pageSize }, setPaginationModel] = useState<GridPaginationModel>({
		pageSize: 100,
		page: 0,
	});
	const defaultSortOrder = {
		field: 'createdAt',
		sort: 'desc',
	};
	const [hasNextPage, setHasNextPage] = useState(false);
	const [sortOrder, setSortOrder] = useState(defaultSortOrder);
	const [filters, setFilters] = useState([]);
	const debouncedSetFilters = useDebounce(setFilters, 500);
	const [rows, setRows] = useState<any[]>([]);
	const [selectedMeta, setSelectedMeta] = useState(null);
	const [selectedMetaName, setSelectedMetaName] = useState(null);

	const { loading: orphanPaymentLogsLoading } = useQuery(LIST_ORPHAN_PAYMENT_LOGS, {
		variables: {
			order: sortOrder,
			limit: pageSize + 1,
			offset: page * pageSize,
			filters,
		},
		context: {
			apiVersion: 'v2',
		},
		onCompleted: (response) => {
			const data = response?.listOrphanPaymentLogs?.data;
			setRows(data?.slice(0, pageSize));
			setHasNextPage(pageSize < data?.length);
		},
	});

	const handleOpenMeta = (meta, name) => {
		setSelectedMeta(meta);
		setSelectedMetaName(name);
	};

	const handleCloseMeta = () => {
		setSelectedMeta(null);
		setSelectedMetaName(null);
	};

	const renderCustomToolbar = () => (
		<GridToolbarContainer>
			<GridToolbarFilterButton />
			<GridToolbarColumnsButton />
			<GridToolbarDensitySelector />
		</GridToolbarContainer>
	);

	const filterOperators = getGridStringOperators().filter(({ value }) => ['contains'].includes(value));

	const columns: GridColDef[] = [
		{
			field: 'paymentReferenceId',
			headerName: 'Payment Reference Id',
			flex: 1,
			type: 'string' as const,
			editable: false,
			filterable: false,
		},
		{
			field: 'paymentMethodType',
			headerName: 'Payment Method',
			flex: 1,
			type: 'string' as const,
			editable: false,
			filterable: false,
		},
		{
			field: 'paymentStatus',
			headerName: 'Status',
			flex: 1,
			type: 'string' as const,
			editable: false,
			filterOperators,
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			type: 'string' as const,
			editable: false,
			filterable: false,
		},
		{
			field: 'last4',
			headerName: 'Last 4',
			flex: 1,
			type: 'string' as const,
			editable: false,
			filterable: false,
		},
		{
			field: 'paymentMeta',
			headerName: 'Payment Meta',
			flex: 1,
			sortable: false,
			filterable: false,
			editable: false,
			renderCell: (params) =>
				params.value ? (
					<Link component='button' onClick={() => handleOpenMeta(params.value, 'Payment')}>
						View
					</Link>
				) : (
					'-'
				),
		},
		{
			field: 'voidMeta',
			headerName: 'Void Meta',
			flex: 1,
			sortable: false,
			filterable: false,
			editable: false,
			renderCell: (params) =>
				params.value ? (
					<Link component='button' onClick={() => handleOpenMeta(params.value, 'Void')}>
						View
					</Link>
				) : (
					'-'
				),
		},
		{
			field: 'createdAt',
			headerName: 'Created At',
			flex: 1,
			type: 'date' as const,
			valueFormatter: adaptGridValueCallback(({ value }) => new Date(Number.parseInt(value, 10)).toUTCString()),
			editable: false,
			filterable: false,
		},
		{
			field: 'updatedAt',
			headerName: 'Updated At',
			flex: 1,
			type: 'date' as const,
			valueFormatter: adaptGridValueCallback(({ value }) => new Date(Number.parseInt(value, 10)).toUTCString()),
			editable: false,
			filterable: false,
		},
	];

	const onSortModelChange = (sortModel: GridSortModel) => {
		if (!sortModel.length || !sortModel[0]?.sort) {
			setSortOrder(defaultSortOrder);
			return;
		}

		setPaginationModel((prev) => ({ ...prev, page: 0 }));

		const { field, sort } = sortModel[0];

		setSortOrder({ field, sort });
	};

	const onFilterModelChange = (filterModel: GridFilterModel) => {
		const { items: filterItems } = filterModel;
		if (!filterItems.length) {
			debouncedSetFilters([]);
			return;
		}
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
		const queryFilters = filterItems
			.map((filter) => {
				const { field, operator, value } = filter;
				if (isEmptyGridFilterValue(value) && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];
				const op = convertMuiFilterForV2ApiWithoutValue(operator);
				return {
					propertyName: field,
					op,
					value: value ?? null,
				};
			})
			.flat();
		debouncedSetFilters(queryFilters);
	};

	return (
		<div className='pca__datagrid-sticky--headfoot'>
			<DataGridPremium
				rows={rows}
				columns={columns}
				slots={{
					toolbar: renderCustomToolbar,
				}}
				onPaginationModelChange={setPaginationModel}
				sx={{ height: '100%' }}
				loading={orphanPaymentLogsLoading}
				rowCount={hasNextPage ? Number.MAX_SAFE_INTEGER : page * pageSize + rows.length}
				pagination
				paginationMode='server'
				paginationModel={{ page, pageSize }}
				pageSizeOptions={[pageSize]}
				sortingMode='server'
				filterMode='server'
				onSortModelChange={onSortModelChange}
				onFilterModelChange={onFilterModelChange}
				slotProps={{
					filterPanel: { logicOperators: [GridLogicOperator.And] },
				}}
				localeText={{
					MuiTablePagination: {
						labelDisplayedRows: ({ from, to }) =>
							`${from}-${to} of ${hasNextPage ? `${to}+` : (page * pageSize) + rows.length}`,
					},
				}}
			/>
			<Dialog open={!!selectedMeta} onClose={handleCloseMeta} maxWidth='md' fullWidth>
				<DialogTitle>
					{selectedMetaName} Meta
					<IconButton
						onClick={handleCloseMeta}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<JSONViewer
						src={selectedMeta}
						name={false}
						collapsed={2}
						enableClipboard
						displayDataTypes={false}
					/>
				</DialogContent>
			</Dialog>
		</div>
	);
};
